import React from 'react';
import {
  getCachedImageUrl,
  releaseCachedImageUrl,
  peekCachedImageUrl,
} from '../utils/imageCache';

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  lazy?: boolean;
};

const CachedImg: React.FC<Props> = ({
  src,
  alt,
  className,
  style,
  onLoad,
  onError,
  loading,
  lazy = true,
  ...rest
}) => {
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  // Init SÍNCRONO desde el caché: si la imagen ya está, se ve al instante (sin
  // pasar por un frame en blanco → sin parpadeo al volver a la pantalla).
  const [displayUrl, setDisplayUrl] = React.useState<string>(
    () => peekCachedImageUrl(src) || '',
  );
  const srcRef = React.useRef<string>('');

  const startFetch = React.useCallback(async () => {
    if (!src) return;
    const currentSrc = src;
    srcRef.current = currentSrc;
    try {
      const { url } = await getCachedImageUrl(currentSrc);
      if (srcRef.current === currentSrc) setDisplayUrl(url);
    } catch {
      if (srcRef.current === currentSrc) setDisplayUrl(currentSrc);
    }
  }, [src]);

  // Al cambiar `src`: si la nueva ya está cacheada, mostrarla YA (sin blanco);
  // si no, blanquear para que entre el placeholder mientras carga. Libera la
  // anterior al desmontar/cambiar (baja el ref; el LRU la mantiene caliente).
  React.useEffect(() => {
    setDisplayUrl(peekCachedImageUrl(src) || '');
    return () => {
      if (srcRef.current) {
        try {
          releaseCachedImageUrl(srcRef.current);
        } catch {}
      }
    };
  }, [src]);

  // Adquiere la imagen (toma el ref + asegura que esté bajada). Si ya está
  // cacheada, corre YA; si no y es lazy, espera a estar cerca del viewport.
  React.useEffect(() => {
    const cached = peekCachedImageUrl(src);
    if (!lazy || cached) {
      void startFetch();
      return;
    }
    const el = imgRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      void startFetch();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            void startFetch();
          }
        });
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy, startFetch, src]);

  // Placeholder gris suave mientras no hay imagen (evita el "ícono roto" al
  // entrar). Se mezcla con el `style` que venga del caller.
  const mergedStyle: React.CSSProperties = displayUrl
    ? style || {}
    : { backgroundColor: '#f3f4f6', ...(style || {}) };

  return (
    <img
      ref={imgRef}
      src={displayUrl || undefined}
      alt={alt}
      className={className}
      style={mergedStyle}
      loading={loading || 'lazy'}
      decoding="async"
      onLoad={onLoad}
      onError={onError}
      {...rest}
    />
  );
};

export default CachedImg;
